import { BackofficeMetricUnit } from '@backoffice-frontend/graphql'
import { useMediaQuery } from '@backoffice-frontend/patterns'
import { useMetricTracking } from '@backoffice-frontend/tracking'
import { useEffect, useRef, useState } from 'react'
import { LoadingLivestream } from '../components/LoadingLivestream'
import { useVideoTiles } from './useVideoTiles'
import { PreviewTile, VideoTile } from './VideoTile'

const BREAKPOINT = '(max-width:1600px)'
const CELLS_HORIZONTAL = 4
const CELLS_VERTICAL = 5
const PREVIEW_WIDTH = 170

export const VideoWall = ({ vehicleId }: { vehicleId: string }) => {
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([])
  const previewRefs = useRef<Array<HTMLCanvasElement | null>>([])
  const tiles = useVideoTiles()
  const [focusedTile, setFocusedTile] = useState(tiles.at(0))
  const isSmallScreen = useMediaQuery(BREAKPOINT)
  const { trackMetric } = useMetricTracking()

  const cameraSelectionStartTime = useRef<number | null>(null)
  const lastSelectedCamera = useRef<string | null>(null)

  const handleCameraSelection = async (
    tile: { id: number; name: string },
    selectionType: 'manual' | 'automatic' = 'manual',
  ) => {
    if (lastSelectedCamera.current && cameraSelectionStartTime.current) {
      const selectionDuration = Date.now() - cameraSelectionStartTime.current
      await trackMetric({
        metricName: 'CameraSelectionTime',
        value: selectionDuration,
        unit: BackofficeMetricUnit.Milliseconds,
        dimensions: [
          { name: 'cameraName', value: lastSelectedCamera.current },
          { name: 'vehicleId', value: vehicleId },
          { name: 'tileId', value: String(focusedTile?.id ?? -1) },
          { name: 'selectionType', value: selectionType },
        ],
        namespace: 'FleetControl/ad-operator-video',
      })
    }

    setFocusedTile(tile)
    lastSelectedCamera.current = tile.name
    cameraSelectionStartTime.current = Date.now()
  }

  const cellsPerRow = isSmallScreen ? CELLS_HORIZONTAL : CELLS_VERTICAL
  const rows = Math.ceil(tiles.length / cellsPerRow)
  const emptyCells = rows * cellsPerRow - tiles.length

  useEffect(() => {
    const videos = videoRefs.current
    const previews = previewRefs.current
    if (!videos || !previews) return

    tiles.forEach(tile => {
      const video = videos[tile.id]
      const preview = previews[tile.id]
      if (!video || !preview) return

      const context = preview.getContext('2d')
      if (!context) return

      const width = video.videoWidth
      const height = video.videoHeight
      preview.width = width
      preview.height = height

      const drawPreview = () => {
        if (video.ended) return
        context.drawImage(video, 0, 0, width, height)
        requestAnimationFrame(drawPreview)
      }

      drawPreview()
    })
  }, [tiles])

  useEffect(() => {
    const trackCameraChange = (
      newTile: { id: number; name: string } | undefined,
    ) => {
      if (lastSelectedCamera.current && cameraSelectionStartTime.current) {
        const selectionDuration = Date.now() - cameraSelectionStartTime.current
        trackMetric({
          metricName: 'CameraSelectionTime',
          value: selectionDuration,
          unit: BackofficeMetricUnit.Milliseconds,
          dimensions: [
            { name: 'cameraName', value: lastSelectedCamera.current },
            { name: 'vehicleId', value: vehicleId },
            { name: 'selectionType', value: 'automatic' },
          ],
          namespace: 'FleetControl/ad-operator-video',
        })
      }

      if (newTile) {
        lastSelectedCamera.current = newTile.name
        cameraSelectionStartTime.current = Date.now()
      } else {
        lastSelectedCamera.current = null
        cameraSelectionStartTime.current = null
      }
    }

    if (focusedTile && tiles.some(tile => tile.id === focusedTile.id)) {
      return
    }

    // focused tile set but not available anymore -> update or remove
    if (focusedTile && !tiles.some(tile => tile.id === focusedTile.id)) {
      const newTile = tiles.length > 0 ? tiles.at(0) : undefined
      trackCameraChange(newTile)
      setFocusedTile(newTile)
      return
    }

    // focused tile NOT set and tiles available -> set
    if (focusedTile === undefined && tiles.length > 0) {
      const newTile = tiles.at(0)
      trackCameraChange(newTile)
      setFocusedTile(newTile)
    }
  }, [tiles, focusedTile, trackMetric, vehicleId])

  useEffect(() => {
    return () => {
      if (lastSelectedCamera.current && cameraSelectionStartTime.current) {
        const selectionDuration = Date.now() - cameraSelectionStartTime.current
        trackMetric({
          metricName: 'CameraSelectionTime',
          value: selectionDuration,
          unit: BackofficeMetricUnit.Milliseconds,
          dimensions: [
            { name: 'cameraName', value: lastSelectedCamera.current },
            { name: 'vehicleId', value: vehicleId },
            { name: 'sessionEnd', value: '1' },
          ],
          namespace: 'FleetControl/ad-operator-video',
        })
      }
    }
  }, [trackMetric, vehicleId])

  return (
    <div
      css={theme => ({
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gridTemplateRows: `repeat(${CELLS_VERTICAL}, 1fr)`,
        gridAutoFlow: 'column',
        [`@media ${BREAKPOINT}`]: {
          gridTemplateColumns: `repeat(${CELLS_HORIZONTAL}, 1fr)`,
          gridTemplateRows: '1fr auto',
          gridAutoFlow: 'row',
        },
        gap: theme.space.Space1,
      })}
    >
      {!focusedTile ? (
        <div
          css={{
            gridColumn: '1 / -1',
            gridRow: '1 / -1',
          }}
        >
          <LoadingLivestream />
        </div>
      ) : (
        <>
          {tiles.map(tile => {
            const isFocused = tile.id === focusedTile?.id
            return (
              <div
                key={`focus-${tile.id}`}
                css={{
                  gridColumn: '1 / 2',
                  gridRow: '1 / -1',
                  [`@media ${BREAKPOINT}`]: {
                    gridColumn: '1 / -1',
                    gridRow: '1 / 2',
                    aspectRatio: '16 / 10',
                    maxHeight: '300px',
                  },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  visibility: isFocused ? 'visible' : 'hidden',
                  width: isFocused ? '100%' : PREVIEW_WIDTH,
                  maxHeight: `calc(${CELLS_VERTICAL} * (${PREVIEW_WIDTH}px / 16 * 10) + ${CELLS_VERTICAL - 1} * var(--space-1))`,
                  color: 'white',
                  backgroundColor: 'black',
                  borderRadius: 8,
                }}
              >
                <VideoTile
                  ref={_ref => {
                    videoRefs.current[tile.id] = _ref
                  }}
                  tileId={tile.id}
                  name={tile.name}
                />
              </div>
            )
          })}

          {tiles.map(tile => {
            const isFocused = tile.id === focusedTile?.id
            return (
              <div
                key={`preview-${tile.id}`}
                css={theme => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  backgroundColor: 'black',
                  overflow: 'hidden',
                  borderRadius: 8,
                  width: PREVIEW_WIDTH,
                  [`@media ${BREAKPOINT}`]: {
                    width: '100%',
                  },
                  aspectRatio: '16 / 10',
                  cursor: 'pointer',
                  boxShadow: isFocused
                    ? `inset 0 0 0 4px ${theme.semantic.ColorContentDefault}`
                    : 'none',
                })}
                onClick={() => handleCameraSelection(tile, 'manual')}
              >
                <PreviewTile
                  ref={_ref => {
                    previewRefs.current[tile.id] = _ref
                  }}
                  name={tile.name}
                  isFocused={isFocused}
                />
              </div>
            )
          })}

          {tiles.length > 0 &&
            Array.from({ length: emptyCells }, (_, index) => (
              <div
                key={`empty-${
                  // biome-ignore lint/suspicious/noArrayIndexKey: The items don't rely on order or state
                  index
                }`}
                css={theme => ({
                  backgroundColor: theme.semantic.ColorSurfaceReduced,
                  borderRadius: 8,
                })}
              />
            ))}
        </>
      )}
    </div>
  )
}
